import React, { useEffect, useMemo, useRef, useState } from 'react';
import useCoarsePointer from '../../hooks/useCoarsePointer';

/**
 * DatePicker — friendly, consistent date selection that replaces native
 * <input type=date>. Two modes:
 *   • mode="single" → ◀ [📅 Wed, 10 Jun 2026 ▾] ▶  (arrows step a day; the
 *     label opens a month-grid popover with a Today shortcut). onChange(iso).
 *   • mode="range"  → one calendar: tap start, tap end → onChange({start,end}).
 *
 * Props: value (iso, single) | range ({start,end}, range), mode, min (iso),
 *        onChange, isDark.
 */

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function todayISO() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}
function parseISO(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}
function toISO(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
function addDays(iso, n) {
  const dt = parseISO(iso) || new Date();
  dt.setDate(dt.getDate() + n);
  return toISO(dt);
}
function fmtLabel(iso) {
  const dt = parseISO(iso);
  if (!dt) return 'Pick a date';
  return `${WD[(dt.getDay() + 6) % 7]}, ${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}
function fmtShort(iso) {
  const dt = parseISO(iso);
  return dt ? `${dt.getDate()} ${MONTHS[dt.getMonth()]}` : '—';
}
// Mon-first weeks of `viewMonth` (a Date at the 1st).
function monthMatrix(view) {
  const first = new Date(view.getFullYear(), view.getMonth(), 1);
  const lead = (first.getDay() + 6) % 7; // 0=Mon
  const start = new Date(first);
  start.setDate(1 - lead);
  const weeks = [];
  let cur = new Date(start);
  for (let w = 0; w < 6; w += 1) {
    const row = [];
    for (let d = 0; d < 7; d += 1) {
      row.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    weeks.push(row);
  }
  return weeks;
}

export default function DatePicker({ value, range, mode = 'single', min, onChange, isDark = false, inline = false }) {
  const [open, setOpen] = useState(false);
  const coarse = useCoarsePointer();
  const dayH = coarse ? '2.5rem' : '2rem';
  const wrapRef = useRef(null);
  const anchorIso = (mode === 'range' ? range?.start : value) || todayISO();
  const [view, setView] = useState(() => {
    const dt = parseISO(anchorIso) || new Date();
    return new Date(dt.getFullYear(), dt.getMonth(), 1);
  });

  // close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const weeks = useMemo(() => monthMatrix(view), [view]);
  const minIso = min || null;

  const isDisabled = (iso) => minIso && iso < minIso;

  const dayBg = (iso, isThisMonth) => {
    if (mode === 'range' && range?.start) {
      if (iso === range.start || iso === range.end) return '#0d9488';
      if (range.end && iso > range.start && iso < range.end) return isDark ? 'rgba(13,148,136,0.25)' : 'rgba(13,148,136,0.12)';
    } else if (iso === value) {
      return '#0d9488';
    }
    if (iso === todayISO()) return isDark ? 'rgba(255,255,255,0.08)' : '#f1f5f9';
    return 'transparent';
  };
  const dayColor = (iso, isThisMonth, selected) => {
    if (selected) return '#fff';
    if (!isThisMonth) return isDark ? '#475569' : '#cbd5e1';
    if (isDisabled(iso)) return isDark ? '#475569' : '#cbd5e1';
    return isDark ? '#e2e8f0' : '#0f172a';
  };

  const pickDay = (iso) => {
    if (isDisabled(iso)) return;
    if (mode === 'range') {
      if (!range?.start || (range.start && range.end)) {
        onChange({ start: iso, end: null });
      } else if (iso < range.start) {
        onChange({ start: iso, end: null });
      } else {
        onChange({ start: range.start, end: iso });
        setOpen(false);
      }
    } else {
      onChange(iso);
      setOpen(false);
    }
  };

  const border = isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)';
  const fieldBg = isDark ? 'rgba(255,255,255,0.06)' : '#ffffff';
  const text = isDark ? '#e8e9ed' : '#111827';

  const navBtn = {
    width: '1.75rem', height: '1.75rem', borderRadius: '0.5rem', cursor: 'pointer',
    border: `1px solid ${border}`, background: fieldBg, color: text, fontSize: '0.875rem',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  };

  const calendarInner = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
        <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() - 1, 1))} style={navBtn}>‹</button>
        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: text }}>{MONTHS[view.getMonth()]} {view.getFullYear()}</span>
        <button type="button" onClick={() => setView(new Date(view.getFullYear(), view.getMonth() + 1, 1))} style={navBtn}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
        {WD.map((w) => (
          <div key={w} style={{ textAlign: 'center', fontSize: '0.5625rem', fontWeight: 600, color: isDark ? '#64748b' : '#94a3b8', padding: '0.125rem 0' }}>{w}</div>
        ))}
        {weeks.flat().map((dt) => {
          const iso = toISO(dt);
          const isThisMonth = dt.getMonth() === view.getMonth();
          const selected = mode === 'range' ? (iso === range?.start || iso === range?.end) : iso === value;
          return (
            <button
              key={iso}
              type="button"
              disabled={isDisabled(iso)}
              onClick={() => pickDay(iso)}
              style={{
                height: dayH, borderRadius: '0.375rem', border: 'none',
                background: dayBg(iso, isThisMonth), color: dayColor(iso, isThisMonth, selected),
                fontSize: '0.75rem', fontWeight: selected ? 700 : 400,
                cursor: isDisabled(iso) ? 'not-allowed' : 'pointer',
              }}
            >{dt.getDate()}</button>
          );
        })}
      </div>
      <button type="button" onClick={() => {
        const t = todayISO();
        setView(() => { const d = parseISO(t); return new Date(d.getFullYear(), d.getMonth(), 1); });
        if (mode === 'single') { onChange(t); setOpen(false); }
      }} style={{
        marginTop: '0.5rem', width: '100%', padding: '0.375rem', borderRadius: '0.5rem',
        border: `1px solid ${border}`, background: isDark ? '#1e293b' : '#f8fafc',
        color: '#0d9488', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
      }}>Today</button>
    </>
  );

  const calendar = inline ? (
    <div style={{
      marginTop: '0.5rem', width: '100%', padding: '0.625rem', borderRadius: '0.75rem',
      background: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc', border: `1px solid ${border}`,
      boxSizing: 'border-box',
    }}>{calendarInner}</div>
  ) : (open && (
    <div style={{
      position: 'absolute', top: 'calc(100% + 0.375rem)', left: 0, zIndex: 60,
      width: '17rem', maxWidth: 'calc(100vw - 1.5rem)', boxSizing: 'border-box',
      padding: '0.625rem', borderRadius: '0.75rem',
      background: isDark ? '#0f172a' : '#ffffff', border: `1px solid ${border}`,
      boxShadow: '0 12px 32px rgba(0,0,0,0.25)',
    }}>{calendarInner}</div>
  ));

  // ── Range field ──
  if (mode === 'range') {
    const summary = range?.start
      ? (range.end
          ? `${fmtShort(range.start)} – ${fmtShort(range.end)} (${Math.round((parseISO(range.end) - parseISO(range.start)) / 86400000) + 1} days)`
          : `${fmtShort(range.start)} – pick end…`)
      : 'Pick start & end';
    return (
      <div ref={wrapRef} style={{ position: 'relative' }}>
        <button type="button" onClick={() => !inline && setOpen((o) => !o)} style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.4375rem 0.625rem', borderRadius: '0.625rem',
          border: `1px solid ${border}`, background: fieldBg, color: text,
          fontSize: '0.8125rem', fontWeight: 600, cursor: inline ? 'default' : 'pointer',
        }}>📅 {summary}{inline ? '' : ' ▾'}</button>
        {calendar}
      </div>
    );
  }

  // ── Single field with day steppers ──
  const prevDisabled = minIso && value && value <= minIso;
  return (
    <div ref={wrapRef} style={{ position: 'relative', display: inline ? 'block' : 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
        <button type="button" disabled={prevDisabled}
          onClick={() => onChange(addDays(value || todayISO(), -1))}
          style={{ ...navBtn, opacity: prevDisabled ? 0.4 : 1, cursor: prevDisabled ? 'not-allowed' : 'pointer' }}>◀</button>
        <button type="button" onClick={() => !inline && setOpen((o) => !o)} style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.4375rem 0.625rem', borderRadius: '0.625rem',
          border: `1px solid ${border}`, background: fieldBg, color: text,
          fontSize: '0.8125rem', fontWeight: 700, cursor: inline ? 'default' : 'pointer', whiteSpace: 'nowrap',
        }}>📅 {fmtLabel(value)}{inline ? '' : ' ▾'}</button>
        <button type="button"
          onClick={() => onChange(addDays(value || todayISO(), 1))}
          style={navBtn}>▶</button>
      </div>
      {calendar}
    </div>
  );
}
